/**
 * Shared authenticated JSON fetch helper.
 *
 * DRYs the token + Bearer + error-parsing boilerplate every domain API repeats.
 * Throws ApiError (carrying response.status) on non-2xx so callers can branch on
 * 404 / 409 / 422 / 429. Returns undefined on 204.
 */
import { API_CONFIG } from '../config/api';
import { getToken } from './tokenStorage';
import { ApiError } from './portfolioApi';
import { logApiRequest, logApiResponse, logApiError } from '../utils/errorHandler';

export { ApiError };

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export async function authedJson<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const url = `${API_CONFIG.baseURL}${path}`;
  const startTime = Date.now();
  const token = await getToken();
  if (!token) {
    logApiError(method, url, new Error('Authentication required'));
    throw new Error('Authentication required');
  }
  logApiRequest(method, url, body);
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const duration = Date.now() - startTime;
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: `${method} ${path} failed` }));
    const message =
      (typeof errorData.detail === 'object' ? errorData.detail?.message : errorData.detail) ||
      `${method} ${path} failed`;
    logApiError(method, url, { ...errorData, status: response.status }, duration);
    throw new ApiError(message, { data: errorData, status: response.status });
  }
  logApiResponse(method, url, response.status, duration);
  if (response.status === 204) return undefined as T;
  return response.json();
}
